import React, { useState } from "react";
import { MapPin, Calendar, Clock, ChevronDown, ChevronUp, ArrowRight, Star, AlertTriangle, Hand } from "lucide-react";
import {
  PROJECT_TYPE_LABEL,
  DIVISION_LABEL,
  PROJECT_LISTING_STATUS_LABEL,
} from "@/data/projectListingTemplates";

const STATUS_TONE = {
  open: "bg-emerald-100 text-emerald-700",
  "reviewing-partners": "bg-amber-100 text-amber-700",
  "walkthrough-scheduled": "bg-violet-100 text-violet-700",
  awarded: "bg-sky-100 text-sky-700",
  closed: "bg-gray-200 text-gray-600",
};

function Meta({ icon: Icon, children }) {
  if (!children) return null;
  return (
    <div className="flex items-center gap-1.5 text-[11px] text-gray-600 uppercase tracking-widest">
      <Icon size={12} className="text-br-bronze" /> {children}
    </div>
  );
}

/**
 * ProjectListingCard
 *
 * Shows a single open project opportunity card on /project-listings.
 * Calls `onApply(listing)` so the parent page can scroll to + prefill the
 * partner-application form.
 */
export default function ProjectListingCard({ listing, onApply }) {
  const [expanded, setExpanded] = useState(false);

  const blocks = [
    { label: "Project Overview", body: listing.overview },
    { label: "Scope Needed", body: listing.scope_needed },
    { label: "Requirements", body: listing.requirements },
    { label: "Timeline", body: listing.timeline_notes },
    { label: "Location Notes", body: listing.location_notes },
  ].filter((b) => b.body && b.body.trim());

  const structured = [
    ["Materials provided", listing.materials_provided],
    ["Labor only", listing.labor_only === true ? "Yes" : listing.labor_only === false ? "No" : ""],
    ["Insurance required", listing.insurance_required === true ? "Yes" : listing.insurance_required === false ? "No" : ""],
    ["License required", listing.license_required],
    ["Crew size", listing.crew_size],
    ["Occupied / vacant", listing.occupied_or_vacant],
    ["Bid due", listing.bid_due_date],
    ["Work hours", listing.work_hours],
    ["Site walk required", listing.site_walk_required ? "Yes" : ""],
  ].filter(([, v]) => v && String(v).trim());

  const statusLabel = PROJECT_LISTING_STATUS_LABEL[listing.status] || listing.status;
  const statusTone = STATUS_TONE[listing.status] || STATUS_TONE.closed;

  return (
    <div
      className={`relative bg-white border rounded-lg overflow-hidden ${listing.is_featured ? "border-br-bronze shadow-xl" : "border-gray-200"}`}
      data-testid={`project-card-${listing.id}`}
    >
      {listing.is_featured && (
        <div className="absolute top-3 right-3 bg-br-bronze text-white text-[9px] tracking-[0.25em] uppercase font-bold px-2 py-1 flex items-center gap-1 rounded">
          <Star size={9} style={{ fill: "#fff" }} /> Featured
        </div>
      )}
      <div className="p-6">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.3em] text-br-bronze font-bold">{PROJECT_TYPE_LABEL[listing.project_type] || listing.project_type}</span>
          <span className="text-[10px] text-gray-400">/</span>
          <span className="text-[10px] uppercase tracking-[0.3em] text-gray-700 font-bold">{DIVISION_LABEL[listing.division] || listing.division}</span>
        </div>
        <h3 className="font-display text-xl sm:text-2xl tracking-widest uppercase mt-1 text-br-black">{listing.title}</h3>

        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5">
          <Meta icon={MapPin}>{listing.city || listing.service_area}</Meta>
          <Meta icon={Calendar}>{listing.start_date}</Meta>
          <Meta icon={Clock}>{listing.duration}</Meta>
          <Meta icon={AlertTriangle}>{listing.urgency}</Meta>
        </div>

        <div className="mt-3">
          <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-bold ${statusTone}`}>
            {statusLabel}
          </span>
        </div>

        {blocks.length > 0 && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="mt-4 inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.25em] text-br-bronze hover:text-br-bronze-2 font-semibold"
            data-testid={`project-expand-${listing.id}`}
          >
            {expanded ? <>Hide details <ChevronUp size={12} /></> : <>View opportunity <ChevronDown size={12} /></>}
          </button>
        )}

        {expanded && (
          <div className="mt-4 space-y-4" data-testid={`project-details-${listing.id}`}>
            {blocks.map((b) => (
              <div key={b.label}>
                <div className="text-[11px] uppercase tracking-[0.22em] text-gray-500 font-bold mb-1.5">{b.label}</div>
                <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{b.body}</p>
              </div>
            ))}
            {structured.length > 0 && (
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-gray-500 font-bold mb-1.5">Project Details</div>
                <div className="grid sm:grid-cols-2 gap-1 text-xs text-gray-700">
                  {structured.map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-2 bg-br-paper/40 border border-gray-100 rounded px-2 py-1">
                      <span className="text-gray-500">{k}</span>
                      <span className="font-semibold text-gray-900">{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mt-5">
          <button
            type="button"
            onClick={() => onApply(listing)}
            className="br-btn-primary"
            data-testid={`project-apply-${listing.id}`}
          >
            <Hand size={14} /> Submit for This Scope <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
